import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, TrendingUp } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Disclaimer, INVESTOR_DISCLAIMER } from "@/components/Disclaimer";
import { api, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const STATUS_STYLE = {
  Available: "bg-emerald-100 text-emerald-800", "Coming Soon": "bg-amber-100 text-amber-800",
  "Under Offer": "bg-lime-100 text-lime-800", Sold: "bg-slate-200 text-slate-600",
};

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useSeo({ title: "Property Opportunities for Investors | Emerald Crown Nottinghamshire", description: "Vetted property opportunities across Nottinghamshire for investors — renovation, BRR, development and rental deals. Capital at risk; figures illustrative, no guaranteed returns.", path: "/opportunities" });

  useEffect(() => {
    track("page_view", { page: "opportunities" });
    api.get("/opportunities").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <div className="pt-20">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24">
        <span className="eyebrow">For investors</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold text-slate-900 leading-tight">Property opportunities for investors</h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">A selection of property opportunities we're assessing across Nottinghamshire and surrounding areas. Register your interest and we'll be in touch with the full details.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        {items.length === 0 && loaded ? (
          <div className="text-center bg-white rounded-2xl border border-slate-200/80 p-14">
            <TrendingUp className="w-12 h-12 text-emerald-700 mx-auto" />
            <h2 className="mt-4 text-2xl font-serif text-slate-900">No live opportunities right now</h2>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">We're actively sourcing. Register your interest and we'll contact you as suitable opportunities become available.</p>
            <Link to="/investment#investor-form" className="mt-6 inline-flex items-center gap-1.5 text-emerald-800 font-medium hover:gap-2.5 transition-all">Register as an investor <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="opportunities-grid">
            {items.map((o, i) => (
              <Reveal key={o.slug}>
                <Link to={`/opportunities/${o.slug}`} data-testid={`opportunity-card-${o.slug}`} className="group block rounded-2xl overflow-hidden border border-slate-200/80 bg-white hover:shadow-xl transition-all duration-300 h-full">
                  <div className="relative h-52 overflow-hidden bg-slate-100">
                    {o.image && <img src={o.image} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <span className={`absolute top-3 left-3 text-[11px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full ${STATUS_STYLE[o.status] || "bg-white text-slate-700"}`}>{o.status}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5" />{o.location} · {o.strategy}</div>
                    <h3 className="mt-2 text-xl font-serif text-slate-900">{o.title}</h3>
                    {o.price_guide && <div className="mt-1 font-mono text-sm text-emerald-800">{o.price_guide}</div>}
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{o.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 group-hover:gap-2.5 transition-all">View & register interest <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
        <div className="mt-8"><Disclaimer text={INVESTOR_DISCLAIMER} testId="opportunities-disclaimer" /></div>
      </section>
    </div>
  );
}
